import { PhotoGrid } from '../photos/PhotoGrid';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { dash } from '@/components/ui/data-table';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  GoneState,
  isNotFound,
  StaleNotice,
  UnresolvedState,
  shownState,
  refreshFailed,
} from '../ui/QueryStates';
import {
  can,
  quoteMachine,
  targetFor,
  parseQuoteLines,
  type LineDraft,
  type LineItemKind,
  type QuoteState,
  type QuoteUpdateInput,
} from '@servwell/shared';
import { PageSkeleton } from '../ui/Skeleton';
import { LineItemsTable } from '../money/LineItemsTable';
import { DocumentLink } from '../money/DocumentLink';
import { ProjectLinkCard } from '../projects/ProjectLinkCard';
import { ServiceTypeStep } from '../servicetypes/ServiceTypeStep';
import { refetchServiceTypes } from '../servicetypes/ServiceTypePicker';
import { CustomerLinkRow } from '../ui/CustomerLinkRow';
import {
  CUSTOMER_EMAIL_POLL_MS,
  CustomerEmailLine,
  customerEmailPending,
  sendButtonState,
} from '../ui/CustomerEmailLine';
import { QUOTE_STATUS, display } from '../status';
import { money } from '../money';
import { IconQuotes } from '../icons';
import { useSession } from '../session';
import { useOrgClock } from '../../lib/useOrgClock';
import { QuoteFollowUpCard } from '../money/QuoteFollowUpCard';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { QuoteLineGrid, QuoteTotalsPreview } from '../money/QuoteLineGrid';
import {
  QuotePlanningFields,
  QuotePlanningSummary,
  quoteWindowInput,
  type QuotePlanningDraft,
  type SavedQuotePlanning,
} from '../money/QuotePlanningFields';
import { Field, field } from '../create/Field';
import { TaxRateField, taxBps } from '../money/TaxRateField';
import { useOnline } from '../ui/useConnection';
import {
  CommercialWriteRecovery,
  useCommercialWriteRecovery,
} from '../jobs/CommercialWriteRecovery';
import {
  WorkOrderConfirmationResources,
  type ConfirmationResources,
} from '../jobs/WorkOrderConfirmationResources';

/**
 * One quote, and the decisions that move it (PRD v2 §5). Which buttons exist is derived
 * from the quote's state, not from hope: the server's machine is the authority, so the
 * page only ever offers a transition the machine would actually accept.
 *
 * Conversion is a LINK, not a state — an approved quote stays approved and grows a job
 * and/or an invoice, both shown here as destinations rather than as a changed status.
 */
export function QuoteDetailPage() {
  const { quoteId } = useParams({ from: '/office/quotes/$quoteId' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useSession().role;
  // The shop's calendar, for the follow-up card's days. The page itself does not wait for it.
  const clock = useOrgClock();
  const q = useQuery({
    ...trpc.quote.get.queryOptions({ quoteId }),
    // The email reads "Sending…" until the drain answers (MAP 249): ask again while it does.
    refetchInterval: (query) =>
      customerEmailPending(query.state.data?.email) ? CUSTOMER_EMAIL_POLL_MS : false,
  });
  const customerQuery = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: q.data?.quote.customerId ?? '' }),
    enabled: Boolean(q.data?.quote.customerId),
  });
  const [declining, setDeclining] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // "Schedule the work" opens a step that asks for the new job's service type
  // (`ServiceTypeStep`, ops insights ticket 04).
  const [scheduling, setScheduling] = useState(false);
  const [serviceTypeId, setServiceTypeId] = useState('');
  const online = useOnline();
  const confirmationRecovery = useCommercialWriteRecovery('workOrder.confirm', quoteId);
  const [resourceDecisions, setResourceDecisions] = useState<ConfirmationResources>({});
  const confirmationPlan = useQuery({
    ...trpc.workOrder.confirmationPlan.queryOptions({ quoteId }),
    enabled:
      confirming &&
      Boolean(q.data?.quote.commercialJobId) &&
      q.data?.quote.status === 'approved' &&
      can(role, 'job', 'create'),
  });
  const confirmationState = shownState(confirmationPlan, confirmationPlan.data);
  const savedConfirmation =
    confirmationRecovery.read.status === 'ready'
      ? confirmationRecovery.read.request
      : null;
  /** The button that opens the step: its Cancel hands focus back here. */
  const scheduleButton = useRef<HTMLButtonElement>(null);

  const refresh = () => void qc.invalidateQueries();
  // The answer names the queued email; what became of it is read back from the quote (`email`,
  // from the outbox, MAP 249), so a reload, another tab and the drain all agree.
  const send = useMutation(trpc.quote.send.mutationOptions({ onSuccess: refresh }));
  const approve = useMutation(trpc.quote.approve.mutationOptions({ onSuccess: refresh }));
  // "Log a follow-up" (ops insights Phase 7): its refusal shows in its own card. One tap, one
  // write: the lock is taken before the key is minted and released when the write settles
  // (`useTapOnce`), since two taps can land in one render, before `isPending` says so.
  const followLock = useTapOnce();
  const followUp = useMutation(
    trpc.quote.followUp.mutationOptions({
      onSuccess: refresh,
      onSettled: () => followLock.release(),
    }),
  );
  const decline = useMutation(
    trpc.quote.decline.mutationOptions({
      onSuccess: () => {
        setDeclining(false);
        refresh();
      },
    }),
  );
  const revise = useMutation(
    trpc.quote.revise.mutationOptions({
      onSuccess: (next) => {
        refresh();
        void navigate({ to: '/quotes/$quoteId', params: { quoteId: next.id } });
      },
    }),
  );
  const toJob = useMutation(
    trpc.quote.toJob.mutationOptions({
      onSuccess: (r) => {
        refresh();
        void navigate({ to: '/jobs/$jobId', params: { jobId: r.job.id } });
      },
      onError: () => refetchServiceTypes(qc, trpc),
    }),
  );
  const confirm = useMutation(
    trpc.workOrder.confirm.mutationOptions({
      onSuccess: async (result, request) => {
        if (!(await confirmationRecovery.complete(request))) return;
        refresh();
        void navigate({
          to: '/work-orders/$workOrderId',
          params: { workOrderId: result.workOrder.id },
        });
      },
      onError: () => {
        refetchServiceTypes(qc, trpc);
        void confirmationPlan.refetch();
      },
    }),
  );
  const confirmWorkOrder = async (picked?: string) => {
    if (
      !online ||
      confirm.isPending ||
      confirmationRecovery.preparing ||
      (!savedConfirmation &&
        (!picked ||
          !confirmationPlan.data ||
          confirmationPlan.isFetching ||
          confirmationPlan.isError ||
          confirmationPlan.isPaused))
    )
      return;
    const request = savedConfirmation ?? {
      quoteId,
      serviceTypeId: picked!,
      ...resourceDecisions,
      ...idem(),
    };
    if (await confirmationRecovery.prepare(request)) confirm.mutate(request);
  };
  const discardConfirmation = async () => {
    if (!(await confirmationRecovery.resolveAttempt())) return;
    if (savedConfirmation) {
      setServiceTypeId(savedConfirmation.serviceTypeId);
      setResourceDecisions({
        propertyId: savedConfirmation.propertyId,
        propertyResolutionReason: savedConfirmation.propertyResolutionReason,
        resourceResolutions: savedConfirmation.resourceResolutions,
      });
    }
    confirm.reset();
    void confirmationPlan.refetch();
  };
  const saveTemplate = useMutation(
    trpc.template.fromQuote.mutationOptions({
      onSuccess: () => {
        setSaving(false);
        setTemplateName('');
        refresh();
      },
    }),
  );
  const toInvoice = useMutation(
    trpc.quote.convert.mutationOptions({
      onSuccess: (invoice) => {
        refresh();
        void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.id } });
      },
    }),
  );

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/quotes" label="Quotes" />
        {/* Deleted, or an old link — gone is not broken, and there is nothing to retry. */}
        {isNotFound(q.error) ? (
          <GoneState
            icon={IconQuotes}
            what="quote"
            backTo="/quotes"
            backLabel="Back to quotes"
          />
        ) : (
          <ErrorState
            icon={IconQuotes}
            what="this quote"
            onRetry={() => void q.refetch()}
          />
        )}
      </PageRoot>
    );
  }
  if (!q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/quotes" label="Quotes" />
        <EmptyState
          icon={IconQuotes}
          title="Quote not found"
          description="It may have been deleted, or the link is stale."
        />
      </PageRoot>
    );
  }

  const { quote, lines, publicUrl, email } = q.data;
  const s = display(QUOTE_STATUS, quote.status);
  // By id — see InvoiceDetailPage.
  const customer = customerQuery.data?.customer;
  const customerName = customer?.displayName ?? null;
  // Off while an email is on its way (MAP 249, as the invoice's), whatever this tab last pressed.
  const sendButton = sendButtonState({
    email,
    sentAt: quote.sentAt,
    pressing: send.isPending,
  });

  const busy =
    editing ||
    send.isPending ||
    approve.isPending ||
    decline.isPending ||
    revise.isPending ||
    toJob.isPending ||
    confirm.isPending ||
    toInvoice.isPending ||
    saveTemplate.isPending ||
    followUp.isPending;
  // Not `toJob`'s: the step draws its own refusal, in the card it answers.
  const error =
    send.error ??
    approve.error ??
    decline.error ??
    revise.error ??
    toInvoice.error ??
    saveTemplate.error;

  /**
   * Which buttons exist, asked of the state machine rather than hardcoded.
   *
   * These were one flag, `draft || sent`, which meant an EXPIRED quote had no Send button —
   * so once the auto-archive automation started expiring quotes on a timer, a shop could
   * watch its pipeline get filed away with no way to un-file it. Sending and deciding are
   * genuinely different sets: `quote.send` is legal from expired, `quote.approve` is not,
   * and a button that throws is worse than a button that isn't there.
   */
  const status = quote.status as QuoteState;
  const canSend = Boolean(targetFor(quoteMachine, status, 'quote.send'));
  const canDecide = Boolean(targetFor(quoteMachine, status, 'quote.approve'));
  const canRevise =
    Boolean(targetFor(quoteMachine, status, 'quote.revise')) &&
    !(quote.commercialJobId && quote.status === 'approved');
  const approved = quote.status === 'approved';
  const canSchedule = approved && !quote.convertedJobId && !quote.commercialJobId;
  // Only a sent quote is out with the customer and waiting: the server refuses the rest.
  const mayFollowUp = quote.status === 'sent' && can(role, 'quote', 'update');

  return (
    <PageRoot>
      <BackCrumb to="/quotes" label="Quotes" />
      <DetailHeader
        title={quote.title ?? 'Untitled quote'}
        badge={<StatusBadge tone={s.tone}>{s.label}</StatusBadge>}
        right={
          <>
            <DocumentLink kind="quote" id={quoteId} label="PDF" />
            {quote.commercialJobId && quote.status === 'draft' && !editing ? (
              <Button variant="ghost" disabled={busy} onClick={() => setEditing(true)}>
                Edit draft
              </Button>
            ) : null}
            {canSend ? (
              <Button
                variant="ghost"
                onClick={() => send.mutate({ quoteId, ...idem() })}
                disabled={busy || sendButton.disabled}
              >
                {sendButton.label}
              </Button>
            ) : null}
            {canDecide ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setDeclining((v) => !v)}
                  disabled={busy}
                >
                  They said no
                </Button>
                <Button
                  onClick={() => approve.mutate({ quoteId, ...idem() })}
                  disabled={busy}
                >
                  {approve.isPending ? 'Recording…' : 'They said yes'}
                </Button>
              </>
            ) : null}
            {canSchedule ? (
              // Toggles the step, like the page's other cards. Opening asks afresh: no type
              // picked on an earlier try, and no refusal left from it. `aria-disabled`, not
              // `disabled`, so the step's Cancel can always hand focus back to it.
              <Button
                ref={scheduleButton}
                aria-expanded={scheduling}
                aria-disabled={busy}
                onClick={() => {
                  if (busy) return;
                  setServiceTypeId('');
                  toJob.reset();
                  setScheduling((v) => !v);
                }}
              >
                Schedule the work
              </Button>
            ) : null}
            {quote.commercialJobId && approved && !quote.convertedJobId ? (
              <Button
                disabled={busy || !online}
                aria-expanded={confirming}
                onClick={() => {
                  confirm.reset();
                  setConfirming((value) => !value);
                }}
              >
                Confirm work order
              </Button>
            ) : null}
            {approved && !quote.convertedInvoiceId ? (
              <Button
                variant="ghost"
                onClick={() => toInvoice.mutate({ quoteId, ...idem() })}
                disabled={busy}
              >
                {toInvoice.isPending ? 'Creating…' : 'Create invoice'}
              </Button>
            ) : null}
            {canRevise ? (
              <Button
                variant="ghost"
                onClick={() => revise.mutate({ quoteId, ...idem() })}
                disabled={busy}
              >
                Revise
              </Button>
            ) : null}
            {lines.length > 0 ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setTemplateName(quote.title ?? '');
                  setSaving((v) => !v);
                }}
                disabled={busy}
              >
                Save as template
              </Button>
            ) : null}
          </>
        }
      />

      {quote.commercialJobId ? (
        <Notice tone="neutral">
          <Link
            to="/jobs/$jobId"
            params={{ jobId: quote.commercialJobId }}
            className="font-semibold hover:underline"
          >
            Open parent Job
          </Link>
          <span className="ml-2">
            {quote.status === 'approved'
              ? 'Accepted scope is immutable. Office confirmation creates its work order; payment is separate.'
              : 'This document belongs to its Job. Revisions stay together; additional scope is a separate quote.'}
          </span>
        </Notice>
      ) : null}
      <MetaStrip
        items={[
          {
            label: 'Quote',
            value: quote.quoteNumber
              ? `#${quote.quoteNumber}${quote.revision > 1 ? ` rev ${quote.revision}` : ''}`
              : 'Draft',
          },
          { label: 'Customer', value: dash(customerName) },
          { label: 'Total', value: money(quote.totalCents) },
        ]}
      />

      {error ? <Notice tone="danger">{error.message}</Notice> : null}
      {quote.commercialJobId ? (
        <CommercialWriteRecovery
          read={confirmationRecovery.read}
          error={confirmationRecovery.error}
          pending={confirm.isPending || confirmationRecovery.preparing}
          online={online}
          onRetry={() => void confirmWorkOrder()}
          onResolve={() => void discardConfirmation()}
        />
      ) : null}
      {confirming && quote.commercialJobId && approved && !quote.convertedJobId ? (
        <SectionCard title="Office confirmation">
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Create a work order from this accepted revision. Available materials will be
              reserved and remaining quantities recorded as backorders. Payment stays
              separate. Schedule its visits after confirmation.
            </p>
            {confirm.error ? (
              <Notice tone="danger" alert>
                {confirm.error.message}
              </Notice>
            ) : null}
            <StaleNotice
              show={
                refreshFailed(confirmationPlan, confirmationPlan.data) ||
                confirmationPlan.isPaused
              }
              onRetry={() => void confirmationPlan.refetch()}
            />
            {confirmationPlan.data ? (
              <WorkOrderConfirmationResources
                plan={confirmationPlan.data}
                value={savedConfirmation ?? resourceDecisions}
                onChange={setResourceDecisions}
                disabled={
                  busy ||
                  !online ||
                  confirmationRecovery.read.status !== 'empty' ||
                  confirmationRecovery.preparing
                }
              />
            ) : confirmationState !== 'ready' ? (
              <UnresolvedState
                state={confirmationState}
                what="confirmation resources"
                icon={IconQuotes}
                error={confirmationPlan.error}
                onRetry={() => void confirmationPlan.refetch()}
              />
            ) : null}
            <ServiceTypeStep
              value={serviceTypeId}
              onChange={setServiceTypeId}
              pending={confirm.isPending}
              locked={
                busy ||
                !online ||
                !confirmationPlan.data ||
                confirmationPlan.isFetching ||
                confirmationPlan.isError ||
                confirmationPlan.isPaused ||
                confirmationRecovery.preparing ||
                confirmationRecovery.read.status !== 'empty'
              }
              label="Confirm work order"
              missing={
                confirmationPlan.data
                  ? [
                      ...(confirmationPlan.data.needsServiceAddress &&
                      !resourceDecisions.propertyId
                        ? ['Service address']
                        : []),
                      ...(confirmationPlan.data.needsServiceAddress &&
                      !resourceDecisions.propertyResolutionReason?.trim()
                        ? ['Address decision reason']
                        : []),
                      ...confirmationPlan.data.lines
                        .filter(
                          (line) =>
                            line.issues.length &&
                            !resourceDecisions.resourceResolutions
                              ?.find((row) => row.quoteLineId === line.quoteLineId)
                              ?.reason.trim(),
                        )
                        .map((line) => `Resource decision reason for ${line.name}`),
                    ]
                  : []
              }
              pendingLabel="Confirming…"
              cancelLabel="Cancel"
              onCancel={() => setConfirming(false)}
              onSubmit={(picked) => void confirmWorkOrder(picked)}
            />
          </div>
        </SectionCard>
      ) : null}

      {/* What became of the latest send, from the outbox (MAP 249): Sending…, Sent, Waiting: …
          or Didn't send: …. The status moves either way — putting a quote in front of somebody
          is a decision the office can carry out by phone. What must not happen is the screen
          implying an email left when none did. */}
      {email ? (
        <CustomerEmailLine email={email}>
          It is marked as sent either way — send them the link below and the approve and
          decline buttons still work.
        </CustomerEmailLine>
      ) : null}

      {/* The job is created only once its service type is picked (`ServiceTypeStep`). */}
      {scheduling && canSchedule ? (
        <SectionCard title="Schedule the work">
          <p className="mb-3 text-sm text-muted-foreground">
            What kind of work is this? The job is created with this service type and opens
            next, where you can schedule a visit.
          </p>
          <div className="space-y-3">
            {/* The refusal, in the card it answers rather than the page's notice far above,
                and announced (`alert`): focus stays on Create, so a screen reader would not
                otherwise hear it. */}
            {toJob.error ? (
              <Notice tone="danger" alert>
                {toJob.error.message}
              </Notice>
            ) : null}
            <ServiceTypeStep
              value={serviceTypeId}
              onChange={setServiceTypeId}
              pending={toJob.isPending}
              // Any other write on this quote (Create invoice, above all): see `locked`.
              locked={busy}
              cancelLabel="Cancel"
              onSubmit={(picked) =>
                toJob.mutate({ quoteId, serviceTypeId: picked, ...idem() })
              }
              onCancel={() => {
                // Cancel goes away with the card, so focus goes back to the button that
                // opened it rather than falling to <body>.
                setScheduling(false);
                scheduleButton.current?.focus();
              }}
            />
          </div>
        </SectionCard>
      ) : null}

      {declining ? (
        <SectionCard title="Record a decline">
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2.5 py-1.5 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why? (price, timing, went with someone else…)"
              aria-label="Reason the quote was declined"
              autoFocus
            />
            <Button
              variant="danger"
              onClick={() =>
                decline.mutate({ quoteId, declinedReason: reason.trim(), ...idem() })
              }
              disabled={busy}
            >
              {decline.isPending ? 'Saving…' : 'Record decline'}
            </Button>
            <Button variant="ghost" onClick={() => setDeclining(false)}>
              Cancel
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Worth writing down — lost-reason is how you find out whether you're pricing
            wrong or just slow to quote.
          </p>
        </SectionCard>
      ) : null}

      {saving ? (
        <SectionCard title="Save this quote as a template">
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2.5 py-1.5 text-sm"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Water heater swap — 40 gal"
              aria-label="Template name"
              autoFocus
            />
            <Button
              onClick={() =>
                saveTemplate.mutate({
                  quoteId,
                  name: templateName.trim(),
                  ...idem(),
                })
              }
              disabled={busy || templateName.trim().length === 0}
            >
              {saveTemplate.isPending ? 'Saving…' : 'Save template'}
            </Button>
            <Button variant="ghost" onClick={() => setSaving(false)}>
              Cancel
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Copies these {lines.length} line{lines.length === 1 ? '' : 's'} and the tax
            rate. Editing this quote later won't change the template.
          </p>
        </SectionCard>
      ) : null}

      {mayFollowUp ? (
        <QuoteFollowUpCard
          sentAt={quote.sentAt}
          followedUpAt={quote.followedUpAt}
          tz={clock.state === 'ready' ? (clock.tz ?? null) : null}
          pending={followUp.isPending}
          locked={busy && !followUp.isPending}
          error={followUp.error?.message ?? null}
          onLog={() => sendOnce(followLock, followUp.mutate, { quoteId })}
        />
      ) : null}

      {quote.status === 'declined' && quote.declinedReason ? (
        <Notice tone="neutral">Declined — {quote.declinedReason}</Notice>
      ) : null}

      {/* The customer's own copy — with the Yes and No buttons on it. Sits above the
          project card because deciding the quote is the point of the page. */}
      {quote.status !== 'draft' ? (
        <SectionCard title="Link for the customer">
          <p className="mb-2 text-sm text-muted-foreground">
            Opens a page showing this quote, where they can approve it or turn it down
            themselves. Anyone with the link can see it.
          </p>
          <CustomerLinkRow kind="quote" id={quoteId} url={publicUrl} />
        </SectionCard>
      ) : null}

      {/* A commercial build is quoted in pieces. Tagging the quote is what makes the
          project's cost rollup start from what was promised, not from what got invoiced. */}
      <ProjectLinkCard
        entityType="quote"
        entityId={quoteId}
        customerId={quote.customerId}
        projectId={quote.projectId}
      />

      {quote.convertedJobId || quote.convertedInvoiceId ? (
        <SectionCard title="Where this went">
          <div className="flex flex-wrap gap-4 text-sm">
            {quote.convertedJobId ? (
              <Link
                to="/work-orders/$workOrderId"
                params={{ workOrderId: quote.convertedJobId }}
                className="font-semibold text-[var(--sw-brand-text)] hover:underline"
              >
                The work order doing this work →
              </Link>
            ) : null}
            {quote.convertedInvoiceId ? (
              <Link
                to="/invoices/$invoiceId"
                params={{ invoiceId: quote.convertedInvoiceId }}
                className="font-semibold text-[var(--sw-brand-text)] hover:underline"
              >
                The invoice for it →
              </Link>
            ) : null}
          </div>
        </SectionCard>
      ) : null}

      {quote.commercialJobId && !editing ? (
        <SectionCard title="Quote responsibility and requested window">
          <QuotePlanningSummary value={quote} />
        </SectionCard>
      ) : null}
      {editing && quote.status === 'draft' ? (
        <QuoteDraftEditor
          key={quote.id}
          quote={quote}
          lines={lines}
          exempt={customer?.taxExempt}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <SectionCard title={`Line items (${lines.length})`}>
          <LineItemsTable
            lines={lines}
            subtotalCents={quote.subtotalCents}
            taxCents={quote.taxCents}
            totalCents={quote.totalCents}
          />
          {quote.commercialJobId ? <QuoteLineIntent lines={lines} /> : null}
        </SectionCard>
      )}
      <PhotoGrid entityType="quote" entityId={quoteId} />
    </PageRoot>
  );
}

type QuoteIntentLine = {
  id: string;
  name: string;
  description: string | null;
  qty: number | string;
  unitPriceCents: number;
  kind: string;
  taxable: boolean;
  priceBookItemId: string | null;
  sourceLocationId: string | null;
  assignedUserId: string | null;
  taskName: string | null;
  unitLabel: string | null;
  estimatedDurationMinutes: number | null;
  requestedStartAt: string | Date | null;
  requestedEndAt: string | Date | null;
};

function QuoteDraftEditor({
  quote,
  lines,
  exempt,
  onDone,
  onCancel,
}: {
  quote: SavedQuotePlanning & {
    id: string;
    version: number;
    title: string | null;
    customerId: string;
    propertyId: string | null;
    taxRateBps: number;
    termsText: string | null;
    validUntil: string | null;
  };
  lines: QuoteIntentLine[];
  exempt?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const trpc = useTRPC();
  const online = useOnline();
  const [title, setTitle] = useState(quote.title ?? '');
  const [planning, setPlanning] = useState<QuotePlanningDraft>({
    estimatorUserId: quote.estimatorUserId ?? undefined,
    requestedStartAt: quote.requestedStartAt
      ? new Date(quote.requestedStartAt).toISOString()
      : undefined,
    requestedEndAt: quote.requestedEndAt
      ? new Date(quote.requestedEndAt).toISOString()
      : undefined,
  });
  const [propertyId, setPropertyId] = useState(quote.propertyId ?? '');
  const propertyChoices = useQuery(
    trpc.customer.get.queryOptions({ customerId: quote.customerId }),
  );
  const [terms, setTerms] = useState(quote.termsText ?? '');
  const [validUntil, setValidUntil] = useState(quote.validUntil ?? '');
  const [taxPct, setTaxPct] = useState(String(quote.taxRateBps / 100));
  const [draft, setDraft] = useState<LineDraft[]>(() =>
    lines.map((line) => ({
      name: line.name,
      description: line.description ?? undefined,
      qty: String(line.qty),
      price: (line.unitPriceCents / 100).toFixed(2),
      kind: line.kind as LineItemKind,
      taxable: line.taxable,
      priceBookItemId: line.priceBookItemId ?? undefined,
      sourceLocationId: line.sourceLocationId ?? undefined,
      assignedUserId: line.assignedUserId ?? undefined,
      taskName: line.taskName ?? undefined,
      unitLabel: line.unitLabel ?? undefined,
      estimatedDurationMinutes: line.estimatedDurationMinutes ?? undefined,
      requestedStartAt: line.requestedStartAt
        ? new Date(line.requestedStartAt).toISOString()
        : undefined,
      requestedEndAt: line.requestedEndAt
        ? new Date(line.requestedEndAt).toISOString()
        : undefined,
    })),
  );
  const initialVersion = useRef(quote.version);
  const intent = useRef<QuoteUpdateInput | null>(null);
  const update = useMutation(
    trpc.quote.updateDraft.mutationOptions({
      onSuccess: onDone,
      onError: (error) => {
        if (error.data?.code === 'BAD_REQUEST' || error.data?.code === 'CONFLICT')
          intent.current = null;
      },
    }),
  );
  const parsed = parseQuoteLines(draft);
  const locked = update.isPending || intent.current !== null;
  const complete = draft.length > 0 && parsed.length === draft.length;
  const save = () => {
    if (!online || update.isPending || !complete) return;
    intent.current ??= {
      quoteId: quote.id,
      expectedVersion: initialVersion.current,
      propertyId: propertyId || null,
      title,
      termsText: terms,
      validUntil: validUntil ? new Date(`${validUntil}T12:00:00`) : null,
      taxRateBps: taxBps(taxPct),
      estimatorUserId: planning.estimatorUserId ?? null,
      requestedStartAt: planning.requestedStartAt
        ? new Date(planning.requestedStartAt)
        : null,
      requestedEndAt: planning.requestedEndAt ? new Date(planning.requestedEndAt) : null,
      lines: parsed,
      ...idem(),
    };
    update.mutate(intent.current);
  };
  return (
    <SectionCard title="Edit draft quote">
      <div className="space-y-4">
        {!online ? (
          <Notice tone="warn">
            Reconnect before saving. Your edits remain in this form.
          </Notice>
        ) : null}
        {update.error ? (
          <Notice tone="danger" alert>
            {update.error.message}
            {update.error.data?.code === 'CONFLICT'
              ? ' Your edits are retained. Cancel editing and reload to use the latest saved revision.'
              : ''}
          </Notice>
        ) : null}
        <fieldset disabled={locked} className="space-y-4">
          <Field label="Service address">
            <select
              className={field}
              value={propertyId}
              onChange={(event) => setPropertyId(event.target.value)}
            >
              <option value="">Choose an address before acceptance</option>
              {(propertyChoices.data?.properties ?? []).map((property) => (
                <option key={property.id} value={property.id}>
                  {property.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quote title">
            <input
              className={field}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <TaxRateField value={taxPct} onChange={setTaxPct} exempt={exempt} />
            <Field label="Valid until">
              <input
                className={field}
                type="date"
                value={validUntil}
                onChange={(event) => setValidUntil(event.target.value)}
              />
            </Field>
          </div>
          <QuotePlanningFields
            value={planning}
            onChange={setPlanning}
            disabled={locked}
          />
          <QuoteLineGrid lines={draft} onChange={setDraft} disabled={locked} />
          <Field label="Terms">
            <textarea
              className={`${field} min-h-20 py-2`}
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
            />
          </Field>
        </fieldset>
        <QuoteTotalsPreview
          customerId={quote.customerId}
          taxRateBps={taxBps(taxPct)}
          lines={parsed}
        />
        <div className="flex gap-2">
          <Button disabled={!online || update.isPending || !complete} onClick={save}>
            {update.isPending
              ? 'Saving…'
              : intent.current
                ? 'Retry this save'
                : 'Save draft'}
          </Button>
          <Button variant="ghost" disabled={locked} onClick={onCancel}>
            Cancel editing
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

/** Read-only source intent survives acceptance; no stock mutation is represented here. */
function QuoteLineIntent({ lines }: { lines: QuoteIntentLine[] }) {
  const trpc = useTRPC();
  const clock = useOrgClock();
  const locations = useQuery(trpc.inventory.locations.queryOptions());
  const members = useQuery(trpc.org.members.queryOptions());
  return (
    <div className="mt-4 border-t border-border pt-4">
      <h3 className="mb-2 text-sm font-semibold">
        Proposed sourcing and responsibilities
      </h3>
      <ul className="divide-y divide-border">
        {lines.map((line) => (
          <li key={line.id} className="grid gap-2 py-3 text-xs sm:grid-cols-4">
            <span className="font-semibold">
              {line.name}
              {line.unitLabel ? ` · ${line.unitLabel}` : ''}
            </span>
            <span>Task: {line.taskName || 'General work'}</span>
            <span>
              Location:{' '}
              {line.sourceLocationId
                ? (locations.data?.find(
                    (location) => location.id === line.sourceLocationId,
                  )?.name ?? 'Selected location unavailable')
                : 'Not proposed'}
            </span>
            <span>
              Assignee:{' '}
              {line.assignedUserId
                ? (members.data?.find((member) => member.userId === line.assignedUserId)
                    ?.name ?? 'Selected member unavailable')
                : 'Unassigned'}
              {line.estimatedDurationMinutes
                ? ` · ${line.estimatedDurationMinutes} min`
                : ''}
            </span>
            {line.requestedStartAt && line.requestedEndAt ? (
              <span className="sm:col-span-4">
                Requested window:{' '}
                {clock.tz
                  ? `${quoteWindowInput(line.requestedStartAt, clock.tz).replace('T', ' ')} – ${quoteWindowInput(line.requestedEndAt, clock.tz).replace('T', ' ')} · ${clock.tz}`
                  : 'Loading shop timezone…'}{' '}
                · no calendar hold
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
